// src/app/(public)/contact/page.tsx
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Link from "next/link";
// Toast is no longer used for success message here
// import { useToast } from "@/hooks/use-toast"; 
import { useState } from "react";
import { Loader2 } from "lucide-react"; // MailCheck removed

const contactFormSchema = z.object({
  fullName: z.string().min(2, { message: "Full name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  subject: z.string().min(5, { message: "Subject must be at least 5 characters." }),
  message: z.string().min(10, { message: "Message must be at least 10 characters." }).max(1000, { message: "Message must not exceed 1000 characters." }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export default function ContactPage() {
  // const { toast } = useToast(); // Toast only needed for errors, which are not handled here.
  const [isLoading, setIsLoading] = useState(false);
  // isSent state is removed as we are redirecting to mail client

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  async function onSubmit(values: ContactFormValues) {
    setIsLoading(true);
    
    const { fullName, email, subject, message } = values;
    
    const mailToSubject = encodeURIComponent(subject);
    const mailToBody = encodeURIComponent(
      `Full Name: ${fullName}\nEmail: ${email}\n\nMessage:\n${message}`
    );
    
    const mailtoHref = `mailto:connect@zamzi.co.za?subject=${mailToSubject}&body=${mailToBody}`;

    // Attempt to open the mail client
    window.location.href = mailtoHref;
    
    // Reset form and loading state.
    // This might execute very quickly after the mailto redirect is initiated.
    // A small timeout can sometimes help ensure the mailto link triggers before reset.
    setTimeout(() => {
        form.reset();
        setIsLoading(false);
    }, 100); 
    // No toast for "Message Sent" as the email client opening is the feedback.
  }

  return (
    <div className="space-y-8">
      <Card className="max-w-2xl mx-auto shadow-lg">
        <CardHeader>
          <h1 className="text-3xl font-bold" style={{ color: "#001489" }}>
            Contact & Support
          </h1>
          <CardDescription>
            Have questions or need assistance? Fill out the form below or check our support channels.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your Full Name" {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subject</FormLabel>
                    <FormControl>
                      <Input placeholder="Reason for your message" {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Please describe your inquiry in detail..."
                        className="min-h-[120px]"
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" style={{ backgroundColor: "hsl(var(--accent))", color: "hsl(var(--accent-foreground))" }} className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLoading ? "Preparing Message..." : "Send Message via Email"}
              </Button>
            </form>
          </Form>

          <div className="mt-8 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold mb-2" style={{ color: "#555555" }}>Other Ways to Get Help</h3>
            <p className="text-sm text-muted-foreground mb-1">
              - Check our <Link href="/faq" className="underline text-primary hover:text-primary/80">FAQ page</Link> for common questions.
            </p>
            <p className="text-sm text-muted-foreground">
              - For urgent matters, you can contact us directly here:
            </p>
            <div className="mt-2">
              <Button
                style={{ backgroundColor: "#34a853", color: "#ffffff" }}
                onClick={() => window.open("https://wa.me/27774584896", "_blank")}
              >
                Contact Us
              </Button>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Button asChild style={{ backgroundColor: '#ffb81c' }} className="text-white hover:bg-blue-700">
              <Link href="/">Back to Home</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
